import { useGlobalDarklyBoolean, GlobalDarklyFlags } from '@oc/darkly/global';
import { useOcDarklyBoolean, OcDarklyFlags } from '@oc/darkly/oc';
import { useLoggedInUser } from 'utils/hooks/useLoggedInUser';
import { isProcessesDefinitionVisible } from 'common/auth/Authorize';
import { useSettingsService } from 'common/settingsService';
import { useFeatureFlags } from 'common/featureFlag/useFeatureFlags';
import { TimesheetSettingsKeyModel } from 'src/graphql/types';

export function useTaeFlags() {
  const {
    timeAndExpenseAuditReports,
    enterpriseProjectTasks,
    timesheetSourceTimeAllocations,
    taeHubsApprovedDateFilter,
    timesheetSettingsBusinessSite,
    taeHubsDateWindow,
    currencyFieldInExpenseCategory,
    taeInvoiceHubImprovements,
    timesheetImportCostCenterFromEngagement,
    engagementSpend,
    importerEngagementRetroChangesValidation,
    requirednessOnFinancialFieldLevel,
    uiNewInvoiceSettingsPage,
    hideInvoicePayments
  } = useOcDarklyBoolean(
    OcDarklyFlags.BooleanFlag.timeAndExpenseAuditReports,
    OcDarklyFlags.BooleanFlag.enterpriseProjectTasks,
    OcDarklyFlags.BooleanFlag.timesheetSourceTimeAllocations,
    OcDarklyFlags.BooleanFlag.taeHubsApprovedDateFilter,
    OcDarklyFlags.BooleanFlag.timesheetSettingsBusinessSite,
    OcDarklyFlags.BooleanFlag.taeHubsDateWindow,
    OcDarklyFlags.BooleanFlag.currencyFieldInExpenseCategory,
    OcDarklyFlags.BooleanFlag.taeInvoiceHubImprovements,
    OcDarklyFlags.BooleanFlag.timesheetImportCostCenterFromEngagement,
    OcDarklyFlags.BooleanFlag.engagementSpend,
    OcDarklyFlags.BooleanFlag.importerEngagementRetroChangesValidation,
    OcDarklyFlags.BooleanFlag.requirednessOnFinancialFieldLevel,
    OcDarklyFlags.BooleanFlag.uiNewInvoiceSettingsPage,
    OcDarklyFlags.BooleanFlag.hideInvoicePayments
  );
  const {
    multipleCostCenters,
    invoicing20,
    dailyUnits,
    requiresActionReasons,
    expenseMultiApproval,
    bulkTimesheetCorrection,
    timesheetAttachments,
    dynamicBatch,
    invoicingSupplierFileConfig,
    monthlyTimesheets
  } = useGlobalDarklyBoolean(
    GlobalDarklyFlags.BooleanFlag.multipleCostCenters,
    GlobalDarklyFlags.BooleanFlag.invoicing20,
    GlobalDarklyFlags.BooleanFlag.dailyUnits,
    GlobalDarklyFlags.BooleanFlag.requiresActionReasons,
    GlobalDarklyFlags.BooleanFlag.expenseMultiApproval,
    GlobalDarklyFlags.BooleanFlag.bulkTimesheetCorrection,
    GlobalDarklyFlags.BooleanFlag.timesheetAttachments,
    GlobalDarklyFlags.BooleanFlag.dynamicBatch,
    GlobalDarklyFlags.BooleanFlag.invoicingSupplierFileConfig,
    GlobalDarklyFlags.BooleanFlag.monthlyTimesheets
  );

  const { isEnabled: isTenantFlagEnabled } = useFeatureFlags(true);
  const { isEnabled } = useSettingsService();
  const msp = isTenantFlagEnabled('msp');
  const tae = isTenantFlagEnabled('time-and-expenses') && isTenantFlagEnabled('time-and-expenses-hub');
  const isDefinitionVisible = isProcessesDefinitionVisible(useLoggedInUser()!);
  // TODO remove once the deployment issue is resolved. Fallbacks to LD FF if the setting is false (not present)
  const invoicingEnabled = isEnabled('timesheets', TimesheetSettingsKeyModel.Invoices);
  const expenses = isEnabled('timesheets', TimesheetSettingsKeyModel.Expenses);
  const hideDeprecatedRates = isEnabled('timesheets', TimesheetSettingsKeyModel.HideDeprecatedRates);
  const rules = isEnabled('timesheets', TimesheetSettingsKeyModel.Rules);
  const timeAttestations = isEnabled('timesheets', TimesheetSettingsKeyModel.TimeAttestationRequired) ?? false;
  const mealBreakAttestations =
    isEnabled('timesheets', TimesheetSettingsKeyModel.MealBreakAttestationRequired) ?? false;

  return {
    /**
     * Main flag to enable all other financial features
     */
    tae,
    /**
     * Shows a Home tab in Timesheet Settings with all financial features
     */
    timesheets: {
      enabled: tae,
      attachments: tae && timesheetAttachments,
      settingsPage: tae,
      settingsPageBusinessSite: tae && timesheetSettingsBusinessSite,
      rules: tae && rules,
      processes: tae && isDefinitionVisible,
      workSchedules: tae,
      holidaySchedules: tae,
      mealBreakAttestations: tae && mealBreakAttestations,
      timeAttestations: tae && timeAttestations,
      sourceTimeAllocations: tae && timesheetSourceTimeAllocations,
      approvedDateFilter: tae && taeHubsApprovedDateFilter,
      dateWindowFilter: tae && taeHubsDateWindow,
      requiresActionReasons: tae && requiresActionReasons,
      bulkTimesheetCorrection: tae && !!bulkTimesheetCorrection,
      validationForTimesheetCorrectionImport: tae && !!bulkTimesheetCorrection,
      monthlyView: !!monthlyTimesheets
    },
    timeCodes: {
      enabled: tae,
      dailyUnits: tae && dailyUnits
    },
    rates: {
      enabled: tae,
      hideDeprecatedRates: hideDeprecatedRates ?? false
    },
    expenses: {
      enabled: tae && expenses,
      approvedDateFilter: tae && taeHubsApprovedDateFilter,
      dateWindowFilter: tae && taeHubsDateWindow,
      currencyField: tae && expenses && currencyFieldInExpenseCategory,
      multiApproval: tae && expenses && expenseMultiApproval,
      attachments: tae && expenses && timesheetAttachments
    },
    spend: {
      enabled: tae && engagementSpend
    },
    invoices: {
      enabled: tae && invoicingEnabled,
      hidePayments: hideInvoicePayments,
      /**
       * @deprecated not in use
       */
      detailsInDrawer: tae && invoicingEnabled && taeInvoiceHubImprovements,
      approvedDateFilter: tae && taeHubsApprovedDateFilter,
      v2: tae && invoicingEnabled && invoicing20,
      dynamicBatch: tae && invoicingEnabled && invoicing20 && dynamicBatch,
      supplierFileConfig: tae && invoicingEnabled && invoicing20 && invoicingSupplierFileConfig,
      newSettingsPage: tae && uiNewInvoiceSettingsPage
    },
    financeFields: {
      enabled: tae,
      requirednessToggle: tae && requirednessOnFinancialFieldLevel
    },
    timeAndExpenseAuditReports: {
      enabled: tae && timeAndExpenseAuditReports
    }, // not in use but still evaluated in LD
    enterpriseProjectTasks: {
      enabled: enterpriseProjectTasks
    },
    msp: {
      enabled: msp
    },
    costCenters: {
      editViaForm: !timesheetImportCostCenterFromEngagement,
      retroChangeImportValidation: tae && importerEngagementRetroChangesValidation,
      multiValued: !!multipleCostCenters
    },
    totalFeeFields: {
      enabled: tae
    }
  };
}
